
import { defineComponent } from 'vue';
import MainLayout from '@/layout/MainLayout.vue';
import SpinnerLoader from '@/components/loader/SpinnerLoader.vue';
import { useAuthStore } from '@/store/AuthStore';
import { useArtistsStore } from '@/store/ArtistsStore';
import { useGalleryStore } from '@/store/GalleriesStore';
import { usePrivateSellersStore } from '@/store/PrivateSellersStore';
import {
  ArtistItem,
  SingleArtistData,
  SelectedExhibition,
  SingleArtworkData,
} from '@/dataTypes/types';
import {
  REGIONS,
  COUNTRIES,
  ART_CATEGORIES,
  ART_THEMES,
  ART_COLORS,
  ART_DESCRIPTIONS,
  ART_SIGNED,
  ART_FRAMED,
  ART_ORIENTATIONS,
  ART_SIZES,
} from '../constants';
import {
  getArtCategoryNames,
  getArtThemeNames,
  getArtColorNames,
  getCountryName,
  getRegionName,
  getArtDescriptionName,
  getArtSignedName,
  getArtFramedName,
  getArtOrientationName,
  getArtSizeName,
  getArtCategoryIds,
  getArtColorIds,
  getArtThemeIds,
  getRegionId,
  getCountryId,
  getArtSizeId,
  getArtOrientationId,
  getArtDescriptionId,
  getArtSignedId,
  getArtFramedId,
} from '@/helpers/nameIdsConverters';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
  getCountriesForRegionName,
  sanitizeCountryForRegion,
} from '@/helpers/regionCountryFiltering';

export default defineComponent({
  components: { MainLayout, SpinnerLoader },
  data() {
    const authStore = useAuthStore();
    const artistsStore = useArtistsStore();
    const galleryStore = useGalleryStore();
    const privateSeller = usePrivateSellersStore();

    let id = +this.$route.params.id;
    if (!id) {
      id = 0;
    }

    return {
      id,
      authStore,
      artistsStore,
      galleryStore,
      privateSeller,
      lastName: '',
      firstName: '',
      regions: REGIONS,
      region: '',
      countries: COUNTRIES,
      country: '',
      imageUrl: '',
      about: '',
      title: '',
      url: '',
      themes: [] as string[],
      themesSelected: [] as string[],
      colors: [] as string[],
      colorsSelected: [] as string[],
      artists: [] as ArtistItem[],
      artist: '',
      artistId: null as number | null,
      artwork: {} as SingleArtworkData,
      exhibitions: [] as SelectedExhibition[],
      artCategories: [] as string[],
      artCategoriesSelected: [] as string[],
      artDescriptions: [] as string[],
      artDescriptionSelected: '',
      year: '',
      height: '',
      width: '',
      depth: '',
      weight: '',
      edition: '',
      details: '',
      certificate: '',
      minimumOfferPrice: '',
      search: '',
      price: '',
      quantity: '1',
      provenance: '',
      condition: 'There are no obvious condition concerns.',
      location: '',
      shipping: 'Ships in up to 14 business days.',
      orientationSelected: '',
      orientations: [] as string[],
      sizeSelected: '',
      sizes: [] as string[],
      signed: [] as string[],
      signedSelected: '',
      framed: [] as string[],
      framedSelected: '',
      uploadedImageFile: null as unknown as File | Blob,
      show: false,
      role: '',
      duplicate: false,
      imageFileName: '',
      isPublishing: false,
    };
  },
  async created() {
    await this.getPageData();
  },

  watch: {
    region() {
      this.country = sanitizeCountryForRegion(
        this.country,
        this.region,
        this.countries,
        this.regions
      );
    },
  },

  methods: {
    async getPageData() {
      if (this.authStore.isLoggedIn) {
        this.role = this.authStore.userRole;
        this.artCategories = ART_CATEGORIES.map((a) => a.name) || [];
        this.themes = ART_THEMES.map((a) => a.name) || [];
        this.colors = ART_COLORS.map((a) => a.name) || [];
        this.artDescriptions = ART_DESCRIPTIONS.map((a) => a.name) || [];
        this.signed = ART_SIGNED.map((a) => a.name) || [];
        this.framed = ART_FRAMED.map((a) => a.name) || [];
        this.orientations = ART_ORIENTATIONS.map((a) => a.name) || [];
        this.sizes = ART_SIZES.map((a) => a.name) || [];
        if (this.role === 'gallery' || this.isPrivateSeller) {
          this.artists = await this.currentStore.fetchMyArtists();

          if (this.$route.params.id) {
            if (this.$route.name == 'duplicate-artwork') {
              this.artwork = await this.currentStore.getArtwork(+this.id);
              this.assignArtistProperties();
              this.id = 0;
              this.duplicate = true;
              return;
            }
            this.artwork = await this.currentStore.getArtwork(+this.id);
            this.assignArtistProperties();
          }
        } else {
          if (this.$route.params.id) {
            this.artwork = await this.artistsStore.fetchArtistArtwork(+this.id);
            this.assignArtistProperties();
          }
        }
      }
    },
    onFileChange(e: any) {
      const file = e.target.files[0];
      this.uploadedImageFile = file;
      this.imageUrl = URL.createObjectURL(file);
      this.imageFileName = file.name;
    },
    duplicateArtwork(id: number) {
      this.$router.push(`/artworks/${id}/duplicate`);
      window.scrollTo(0, 0);
      this.getPageData();
    },
    assignArtistProperties() {
      this.title = !this.isDuplicate ? this.artwork?.title : '';
      this.artist = this.artwork.artist.full_name || '';
      this.artistId = this.artwork.artist.id || null;
      this.artCategoriesSelected = getArtCategoryNames(
        this.artwork.art_categories
      );
      this.themesSelected = getArtThemeNames(this.artwork.art_themes) || [];
      this.colorsSelected = getArtColorNames(this.artwork.art_colors) || [];
      this.country = this.getCountryName(this.artwork.country) || '';
      this.region = this.getRegionName(this.artwork.region) || '';
      this.imageUrl = !this.isDuplicate ? this.artwork?.image?.original : '';
      this.year = this.artwork.year_range || '';
      this.height = this.artwork.height || '';
      this.width = this.artwork.width || '';
      this.depth = this.artwork.depth || '';
      this.weight = this.artwork.weight || '';
      this.artDescriptionSelected = getArtDescriptionName(
        this.artwork.art_description
      );
      this.edition = this.artwork.edition_of || '';
      this.details = this.artwork.details;
      this.certificate = (this.artwork as any).certificate || '';
      this.minimumOfferPrice = (this.artwork as any).minimum_offer_price || '';
      this.signedSelected = this.getArtSignedName(this.artwork.signed) || '';
      this.framedSelected = this.getArtFramedName(this.artwork.framed) || '';
      this.price = this.artwork.price || '';
      this.quantity = String(this.artwork.quantity) || this.quantity;
      this.provenance = this.artwork.provenance || '';
      this.condition = this.artwork.condition_report || this.condition;
      this.location = this.artwork.location || '';
      this.shipping = this.artwork.shipping || this.shipping;
      this.orientationSelected =
        this.getArtOrientationName(this.artwork.art_orientation) || '';
      this.sizeSelected = this.getArtSizeName(this.artwork.art_size) || '';
    },
    getArtCategoryNames,
    getArtThemeNames,
    getArtColorNames,
    getCountryName,
    getRegionName,
    getArtDescriptionName,
    getArtSignedName,
    getArtFramedName,
    getArtOrientationName,
    getArtSizeName,
    getArtCategoryIds,
    getArtColorIds,
    getArtThemeIds,
    getRegionId,
    getCountryId,
    getArtSizeId,
    getArtOrientationId,
    getArtDescriptionId,
    getArtSignedId,
    getArtFramedId,
    deleteArtwork() {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#000',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, remove it!',
      }).then((result) => {
        if (result.isConfirmed) {
          if (this.role === 'gallery' || this.isPrivateSeller) {
            this.deleteGalleryArtwork();
          } else {
            this.deleteArtistArtwork();
          }
        }
      });
    },
    async deleteGalleryArtwork() {
      const response = this.isPrivateSeller
        ? await this.privateSeller.deleteArtwork(this.id)
        : await this.galleryStore.deleteArtwork('galleries', +this.artwork.id);
      if (response.success === true) {
        Swal.fire('Removed!', 'Your artwork has been removed.', 'success');
      } else {
        Swal.fire('Error!', 'Something went wrong.', 'error');
      }
      this.$router.push('/artworks');
    },
    async deleteArtistArtwork() {
      const response = await this.galleryStore.deleteArtwork(
        'artists',
        +this.artwork.id
      );
      if (response.success === true) {
        Swal.fire('Removed!', 'Your artwork has been removed.', 'success');
      } else {
        Swal.fire('Error!', 'Something went wrong.', 'error');
      }
      this.$router.push('/artworks');
    },
    async updateOrAddArtwork() {
      this.isPublishing = true;
      const signedId = this.signedSelected
        ? this.getArtSignedId(this.signedSelected)
        : null;
      const framedId = this.framedSelected
        ? this.getArtFramedId(this.framedSelected)
        : null;
      const sizeId = this.sizeSelected
        ? this.getArtSizeId(this.sizeSelected)
        : null;
      const orientationId = this.orientationSelected
        ? this.getArtOrientationId(this.orientationSelected)
        : null;
      const descriptionId = this.artDescriptionSelected
        ? this.getArtDescriptionId(this.artDescriptionSelected)
        : null;
      const regionId = this.region ? String(getRegionId(this.region)) : null;
      const countryId = this.country
        ? String(getCountryId(this.country))
        : null;
      const colorsIds = this.colorsSelected
        ? this.getArtColorIds(this.colorsSelected)
        : null;
      const artCategoriesIds = this.artCategoriesSelected
        ? this.getArtCategoryIds(this.artCategoriesSelected)
        : null;
      const themesIds = this.themesSelected
        ? getArtThemeIds(this.themesSelected)
        : null;
      const artwork = {
        title: this.title,
        artist_id: this.artistId,
        art_category_ids: artCategoriesIds,
        art_color_ids: colorsIds,
        art_theme_ids: themesIds,
        ...(typeof regionId === 'string' && { region_id: regionId.toString() }),
        ...(typeof countryId === 'string' && {
          country_id: countryId.toString(),
        }),
        ...(typeof sizeId === 'number' && { art_size_id: sizeId.toString() }),
        ...(typeof orientationId === 'number' && {
          art_orientation_id: orientationId,
        }),
        ...(typeof descriptionId === 'number' && {
          art_description_id: descriptionId,
        }),
        year_range: this.year,
        details: this.details,
        ...(typeof signedId === 'number' && { art_signed_id: signedId }),
        ...(typeof framedId === 'number' && { art_framed_id: framedId }),
        is_for_charity: false,
        shipping: this.shipping,
        provenance: this.provenance,
        condition_report: this.condition,
        location: this.location,
        height: this.height,
        width: this.width,
        depth: this.depth,
        weight: this.weight,
        edition_of: this.edition,
        price: this.price,
        quantity: this.quantity,
        certificate: this.certificate,
        minimum_offer_price: this.minimumOfferPrice,
      };

      const formData = new FormData();
      Object.entries(artwork).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Handle arrays
          value.forEach((item) => {
            formData.append(key + '[]', item.toString());
          });
        } else if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          // Handle string, number, and boolean
          formData.append(key, value.toString());
        }
      });
      if (this.uploadedImageFile && this.imageFileName) {
        formData.append('image', this.uploadedImageFile, this.imageFileName);
      }

      const validationErrors: any[] = [];

      if (artwork.title === '') {
        validationErrors.push('title must have a value');
      }

      if (
        (this.role === 'gallery' || this.isPrivateSeller) &&
        !artwork.artist_id
      ) {
        validationErrors.push('artist must have a value');
      }

      if (!Array.isArray(artwork.art_category_ids)) {
        validationErrors.push('art category must have a value');
      }

      if (!Array.isArray(artwork.art_color_ids)) {
        validationErrors.push('colors must have a value');
      }

      if (!Array.isArray(artwork.art_theme_ids)) {
        validationErrors.push('theme must have a value');
      }

      if (!artwork.region_id) {
        validationErrors.push('region must have a value');
      }

      if (!artwork.country_id) {
        validationErrors.push('country must have a value');
      }

      if (!artwork.art_size_id) {
        validationErrors.push('size must have a value');
      }

      if (!artwork.art_orientation_id) {
        validationErrors.push('orientation must have a value');
      }

      if (!artwork.art_description_id) {
        validationErrors.push('description must have a value');
      }

      if (validationErrors.length > 0) {
        Swal.fire({
          title: 'Error',
          text: `There are ${
            validationErrors.length
          } errors: ${validationErrors.join(', ')}`,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#000',
        });

        this.isPublishing = false;
        return;
      }

      if (
        this.role === 'gallery' ||
        this.isPrivateSeller ||
        this.role === 'artist'
      ) {
        try {
          if (this.id) {
            if (this.role === 'artist') {
              await this.artistsStore.updateArtwork(this.id, formData);
            } else {
              await this.currentStore.updateArtwork(+this.id, formData);
            }
          } else {
            if (this.role === 'artist') {
              await this.artistsStore.createArtwork(formData);
            } else {
              await this.currentStore.createArtwork(formData);
            }
          }
          Swal.fire({
            title: 'Success',
            text: `Artwork data ${this.id ? 'updated' : 'added'} successfully`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#000',
          });
          this.$router.back();
        } catch (error) {
          Swal.fire({
            title: 'Error',
            text: `Error ${this.id ? 'updating' : 'adding'} artwork data: ${
              (error as { message?: string }).message
            }`,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#000',
          });
        } finally {
          this.isPublishing = false;
        }
      }
    },
  },
  computed: {
    isPrivateSeller() {
      return this.authStore.userRole === 'private_seller';
    },
    currentStore() {
      return this.isPrivateSeller ? this.privateSeller : this.galleryStore;
    },
    isDuplicate(): boolean {
      return this.$route.fullPath.includes('duplicate');
    },
    filteredCountries() {
      return getCountriesForRegionName(
        this.region,
        this.countries,
        this.regions
      );
    },
  },
});
